import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import type {
  SearchConversationHit,
  SearchNoteHit,
  SearchNoteResult,
} from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import styles from '../../../searchResults.module.scss';
import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';
import { SearchResultsPagination } from '../../pagination/searchResultsPagination';
import { SearchResultsSort } from '../../sort/searchResultsSort';
import { ConversationPreview } from '../conversations/components/preview/conversationPreview';

import { NoteResult } from './components/result/noteResult';

type NotesResultsProps = {
  result?: SearchNoteResult;
};

/**
 * Represents a component that is used to render the notes tab of the search
 * results
 */
export const NotesResults = ({ result }: NotesResultsProps): JSX.Element => {
  const [previewedHit, setPreviewedHit] = useState<SearchNoteHit | null>(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedHit,
  });

  const handleNavigateToConversation = useCallback(() => {
    if (!previewedHit) return;
    ampli.searchNotePreviewGoToClicked();
  }, [previewedHit]);

  const handleRowClick = useCallback(
    (hit: SearchNoteHit) => {
      if (hit.numeric_id === previewedHit?.numeric_id) return;

      setPreviewedHit(hit);
      ampli.searchNotePreviewOpened();
    },
    [previewedHit, setPreviewedHit],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedHit(null);
    ampli.searchNotePreviewClosed();
  }, [setPreviewedHit]);

  const hitsCount = result?.hits.length ?? 0;
  if (hitsCount === 0) {
    return <SearchResultsEmpty />;
  }

  /**
   * `ConversationPreview` component expects _inner_hits, but for notes the API
   * returns _highlights. This conversion is done so we can use that component
   * for both conversations and notes.
   */
  const highlightsToInnerHits = previewedHit?._highlights?.message?.length
    ? [
        {
          id: previewedHit.id,
          highlight: {
            text: previewedHit._highlights.message[0] ?? '',
          },
        },
      ]
    : [];

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom noMinHeight>
          <Column.Slot placement="end">
            <SearchResultsSort />
          </Column.Slot>
        </Column.Row>

        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {result?.hits.map((hit, index) => {
            return (
              <NoteResult
                className={classnames(styles.rowClickable, {
                  [styles.rowSelected]: hit.id === previewedHit?.id,
                })}
                key={`${hit.id}-${index}`}
                hit={hit}
                onRowClick={handleRowClick}
              />
            );
          })}
        </Column.Row>

        <Column.Row noVerticalPadding noHorizontalPadding borderTop>
          <SearchResultsPagination
            pageCount={result?.nb_pages ?? 1}
            currentPage={result?.page ?? 0}
          />
        </Column.Row>
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedHit ? (
            <ConversationPreview
              csid={previewedHit.numeric_id}
              highlights={
                previewedHit._highlights as SearchConversationHit['_highlights']
              }
              innerHits={highlightsToInnerHits}
              onGoToConversation={handleNavigateToConversation}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a note"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of the note you&apos;re looking for?{' '}
                      <br /> Go ahead and click a note to see it here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
